import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Brain, Sparkles, FileText, Mic } from 'lucide-react';
import { usePageHeader } from '../context/PageHeaderContext';

const CHARACTERS = [
  {
    id: 'mena',
    name: 'Mena ORB',
    image: 'https://customer-assets.emergentagent.com/job_orb-management-app/artifacts/67lqvc7a_Mena%20Standard.png',
    color: '#8B5CF6',
  },
  {
    id: 'katie',
    name: 'Katie ORB',
    image: 'https://customer-assets.emergentagent.com/job_orb-management-app/artifacts/b90ayuat_katie%20standard.png',
    color: '#EC4899',
  },
  {
    id: 'luke',
    name: 'Luke ORB',
    image: 'https://customer-assets.emergentagent.com/job_orb-management-app/artifacts/ue5pv5dv_Luke%20Standard.png',
    color: '#3B82F6',
  },
  {
    id: 'dorian',
    name: 'Dorian ORB',
    image: 'https://customer-assets.emergentagent.com/job_orb-management-app/artifacts/bi6kcptf_Dorian%20Standard.png',
    color: '#F59E0B',
  },
  {
    id: 'octavio',
    name: 'Octavio ORB',
    image: 'https://customer-assets.emergentagent.com/job_orb-management-app/artifacts/zi3ibx7q_Octavio%20Standard.png',
    color: '#10B981',
  },
  // Order matters — controls tile placement in the grid below:
  //   Mobile (2 cols):                 Desktop (3 cols):
  //     Mena   |  Katie                 Mena  |  Katie |  Luke
  //     Luke   |  Dorian                Dorian |  Octavio | Call Recorder
  //     Octavio| Call Recorder          Chat Summarizer | User Summarizer
  //     Chat Summ | User Summ
  {
    id: 'call_recorder',
    name: 'Call Recorder',
    image: null,
    icon: 'mic',
    color: '#DC2626',
  },
  {
    id: 'summarizer',
    name: 'Chat Summarizer',
    image: null,
    icon: 'summarizer',
    color: '#0EA5E9',
    isInternal: true,
  },
  {
    id: 'user_summarizer',
    name: 'User Summarizer',
    image: null,
    icon: 'summarizer',
    color: '#7C3AED',
    isInternal: true,
  },
];

export default function AITrainingHubPage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  usePageHeader({ title: 'AI Agent Training' }, []);

  if (user && user.role !== 'admin' && (user.feature_toggles || {}).admin_ai_training !== true) {
    return (
      <div className="container max-w-3xl mx-auto p-8 text-center">
        <p className="text-muted-foreground">Admin only.</p>
      </div>
    );
  }

  return (
    <div className="container max-w-5xl mx-auto px-4 py-8" data-testid="ai-training-hub">
      {/* ORB AI Assistant — global */}
      <button
        onClick={() => navigate('/admin/ai-training/orb')}
        className="w-full mb-3 group flex items-center gap-5 rounded-2xl bg-gradient-to-br from-[#586D52] to-[#3a4d36] text-white p-5 shadow-lg active:scale-[0.99] transition-transform"
        data-testid="train-orb-assistant-btn"
      >
        <div className="h-16 w-16 rounded-2xl bg-white/15 backdrop-blur flex items-center justify-center border border-white/20">
          <Sparkles className="w-8 h-8" />
        </div>
        <div className="flex-1 text-left">
          <p className="text-xs uppercase tracking-[0.2em] text-white/70 font-semibold">Global Training</p>
          <p className="text-lg font-bold leading-tight">ORB Ai Assistant</p>
          <p className="text-xs text-white/80 mt-0.5">Knowledge added here is shared with every character</p>
        </div>
        <Brain className="w-6 h-6 text-white/70 group-hover:text-white transition-colors" />
      </button>

      {/* Phone IVR Voice Agent */}
      <button
        onClick={() => navigate('/admin/ai-training/voice-agent')}
        className="w-full mb-8 group flex items-center gap-5 rounded-2xl bg-gradient-to-br from-[#3b82f6] to-[#1e40af] text-white p-5 shadow-lg active:scale-[0.99] transition-transform"
        data-testid="train-voice-agent-btn"
      >
        <div className="h-16 w-16 rounded-2xl bg-white/15 backdrop-blur flex items-center justify-center border border-white/20">
          <Mic className="w-8 h-8" />
        </div>
        <div className="flex-1 text-left">
          <p className="text-xs uppercase tracking-[0.2em] text-white/70 font-semibold">Phone IVR</p>
          <p className="text-lg font-bold leading-tight">AI Voice Agent</p>
          <p className="text-xs text-white/80 mt-0.5">System prompt, do/don't say, knowledge, and TTS voice</p>
        </div>
        <Brain className="w-6 h-6 text-white/70 group-hover:text-white transition-colors" />
      </button>

      <h2 className="text-sm uppercase tracking-[0.2em] text-muted-foreground font-semibold mb-3">Characters</h2>

      <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
        {CHARACTERS.map((c) => (
          <button
            key={c.id}
            onClick={() => navigate(`/admin/ai-training/${c.id}`)}
            className="group flex flex-col items-center text-center rounded-2xl bg-card border border-border p-4 shadow-sm hover:shadow-md active:scale-[0.98] transition-all"
            data-testid={`train-character-${c.id}`}
          >
            <div
              className="h-28 w-24 rounded-xl overflow-hidden border-2 mb-3 flex items-center justify-center"
              style={{ borderColor: c.color, background: c.image ? 'transparent' : `${c.color}15` }}
            >
              {c.image ? (
                <img
                  src={c.image}
                  alt={c.name}
                  className="w-full h-full object-cover object-top"
                  style={{ aspectRatio: '3/4' }}
                />
              ) : c.icon === 'mic' ? (
                <Mic className="w-10 h-10" style={{ color: c.color }} />
              ) : (
                <FileText className="w-10 h-10" style={{ color: c.color }} />
              )}
            </div>
            <p className="font-bold text-sm">{c.name}</p>
            <p className="text-[11px] text-muted-foreground mt-1 group-hover:text-foreground transition-colors">
              {c.isInternal ? 'Internal — used for all summaries' : 'Tap to train'}
            </p>
          </button>
        ))}
      </div>
    </div>
  );
}
